import { CheckOutlined, UploadOutlined } from '@ant-design/icons'
import { App, Badge, Button, Card, Input, Select, Space, Table, Tag, Tooltip } from 'antd'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  DOMAIN_LABELS,
  DOMAINS,
  MIN_SAMPLE_FOR_STATS,
  QUESTION_STATUS_LABELS,
  QUESTION_STATUSES,
  QUESTION_TYPE_LABELS,
  QUESTION_TYPES,
  STAGE_META,
  STAGES,
  type QuestionStatus,
  type QuestionType,
} from '@baby/shared'

import { questionApi } from '../../api/questions'
import { RequestError } from '../../api/client'
import type { QuestionQuery, QuestionRow } from '../../api/types'
import { useAuth } from '../../store/auth'

/** 状态色。ACTIVE 用绿色是唯一重要的一处 —— 一眼看出哪些题正在被真实用户看到 */
const STATUS_COLOR: Record<QuestionStatus, string> = {
  DRAFT: 'default',
  PENDING_REVIEW: 'orange',
  REVIEWED: 'blue',
  ACTIVE: 'green',
  ARCHIVED: 'red',
}

export default function QuestionList({ fixedStatus }: { fixedStatus?: QuestionStatus }) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { message, modal } = App.useApp()
  const role = useAuth((state) => state.me?.role)
  const canBulkReview =
    fixedStatus === 'PENDING_REVIEW' && (role === 'REVIEWER' || role === 'SUPER')
  const canBulkPublish = !fixedStatus && (role === 'CONTENT' || role === 'SUPER')
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [query, setQuery] = useState<QuestionQuery>({
    page: 1,
    pageSize: 20,
    status: fixedStatus,
  })
  const requestQuery = fixedStatus ? { ...query, status: fixedStatus } : query

  const { data, isFetching } = useQuery({
    queryKey: ['questions', requestQuery],
    queryFn: () => questionApi.list(requestQuery),
  })

  const bulkReview = useMutation({
    mutationFn: (ids: string[]) => questionApi.bulkReview(ids),
    onSuccess: async ({ approvedCount }) => {
      setSelectedIds([])
      await queryClient.invalidateQueries({ queryKey: ['questions'] })
      void message.success(`已通过 ${approvedCount} 道题`)
    },
    onError: (error) => void message.error(error instanceof Error ? error.message : '批量复核失败'),
  })

  const bulkPublish = useMutation({
    mutationFn: (ids: string[]) => questionApi.bulkPublish(ids),
    onSuccess: async ({ publishedCount }) => {
      setSelectedIds([])
      await queryClient.invalidateQueries({ queryKey: ['questions'] })
      void message.success(`已上架 ${publishedCount} 道题`)
    },
    onError: (error) => {
      const detail = error instanceof RequestError ? error.body.detail : null
      if (Array.isArray(detail)) {
        modal.error({
          title: error.message,
          content: (
            <ul style={{ paddingLeft: 18, margin: 0 }}>
              {detail.map((item, index) => (
                <li key={index}>{String(item)}</li>
              ))}
            </ul>
          ),
        })
        return
      }
      void message.error(error instanceof Error ? error.message : '批量上架失败')
    },
  })

  const patch = (p: Partial<QuestionQuery>) => setQuery((q) => ({ ...q, ...p, page: 1 }))

  return (
    <Card
      title={fixedStatus ? QUESTION_STATUS_LABELS[fixedStatus] : '题库'}
      extra={<span style={{ color: '#999' }}>当前筛选共 {data?.total ?? 0} 题</span>}
    >
      <Space wrap style={{ marginBottom: 16 }}>
        {canBulkReview && (
          <Button
            type="primary"
            icon={<CheckOutlined />}
            disabled={selectedIds.length === 0}
            loading={bulkReview.isPending}
            onClick={() =>
              modal.confirm({
                title: `确认通过已选择的 ${selectedIds.length} 道题？`,
                content: '通过后题目进入“已复核”状态，仍需内容运营上架后才会进入用户题池。',
                okText: '确认通过',
                cancelText: '取消',
                onOk: () => bulkReview.mutateAsync(selectedIds),
              })
            }
          >
            批量通过{selectedIds.length > 0 ? `（${selectedIds.length}）` : ''}
          </Button>
        )}
        {canBulkPublish && (
          <Button
            type="primary"
            icon={<UploadOutlined />}
            disabled={selectedIds.length === 0}
            loading={bulkPublish.isPending}
            onClick={() =>
              modal.confirm({
                title: `确认上架已选择的 ${selectedIds.length} 道题？`,
                content: '上架后题目会立即进入用户题池。系统将再次执行全部上架前置校验。',
                okText: '确认上架',
                cancelText: '取消',
                onOk: () => bulkPublish.mutateAsync(selectedIds),
              })
            }
          >
            批量上架{selectedIds.length > 0 ? `（${selectedIds.length}）` : ''}
          </Button>
        )}
        <Input.Search
          allowClear
          placeholder="搜 id 或题干"
          style={{ width: 240 }}
          onSearch={(keyword) => patch({ keyword })}
        />
        {!fixedStatus && (
          <Select
            allowClear
            placeholder="状态"
            style={{ width: 140 }}
            onChange={(status) => patch({ status })}
            options={QUESTION_STATUSES.map((s) => ({
              value: s,
              label: QUESTION_STATUS_LABELS[s],
            }))}
          />
        )}
        <Select
          allowClear
          placeholder="题型"
          style={{ width: 120 }}
          onChange={(type) => patch({ type })}
          options={QUESTION_TYPES.map((type) => ({
            value: type,
            label: QUESTION_TYPE_LABELS[type],
          }))}
        />
        <Select
          allowClear
          placeholder="知识域"
          style={{ width: 140 }}
          onChange={(domain) => patch({ domain })}
          options={DOMAINS.map((d) => ({ value: d, label: DOMAIN_LABELS[d] }))}
        />
        <Select
          allowClear
          placeholder="科目"
          style={{ width: 160 }}
          onChange={(stage) => patch({ stage })}
          options={STAGES.map((s) => ({
            value: s,
            label: `${STAGE_META[s].subject} · ${STAGE_META[s].months}`,
          }))}
        />
        <Select
          allowClear
          placeholder="需专业复核"
          style={{ width: 140 }}
          onChange={(v) => patch({ needsExpertReview: v })}
          options={[
            { value: true, label: '需复核' },
            { value: false, label: '无需复核' },
          ]}
        />
      </Space>

      <Table<QuestionRow>
        rowKey="id"
        size="small"
        loading={isFetching}
        dataSource={data?.items ?? []}
        rowSelection={
          canBulkReview || canBulkPublish
            ? {
                preserveSelectedRowKeys: true,
                selectedRowKeys: selectedIds,
                getCheckboxProps: (row) => ({
                  disabled: canBulkPublish && row.status !== 'REVIEWED',
                }),
                onChange: (keys) => {
                  if (keys.length > 100) {
                    void message.warning('单次最多选择 100 道题')
                    return
                  }
                  setSelectedIds(keys.map(String))
                },
              }
            : undefined
        }
        pagination={{
          current: data?.page,
          pageSize: data?.pageSize,
          total: data?.total,
          showSizeChanger: true,
          pageSizeOptions: [20, 50, 100],
          showTotal: (total) => `当前筛选共 ${total} 题`,
        }}
        onChange={(pagination, _filters, sorter) => {
          const currentSorter = Array.isArray(sorter) ? sorter[0] : sorter
          const sortOrder =
            currentSorter?.order === 'ascend'
              ? 'asc'
              : currentSorter?.order === 'descend'
                ? 'desc'
                : undefined
          setQuery((current) => ({
            ...current,
            page: pagination.current ?? 1,
            pageSize: pagination.pageSize ?? current.pageSize,
            sortBy: sortOrder ? 'wrongRate' : undefined,
            sortOrder,
          }))
        }}
        columns={[
          { title: 'id', dataIndex: 'id', width: 110 },
          {
            title: '题干',
            dataIndex: 'question',
            ellipsis: true,
            render: (text: string, row) => (
              <Space size={4}>
                {row.isTrap && (
                  <Tooltip title="民间误区陷阱题">
                    <Tag color="volcano">坑</Tag>
                  </Tooltip>
                )}
                {row.needsExpertReview && (
                  <Tooltip title="需儿科 / 产科专业人士复核，未复核不得上架">
                    <Tag color="gold">医</Tag>
                  </Tooltip>
                )}
                {row.isCriticalSafety && (
                  <Tooltip title="关键安全题，错误会在用户能力报告中独立提示">
                    <Tag color="red">安全</Tag>
                  </Tooltip>
                )}
                <span>{text}</span>
              </Space>
            ),
          },
          {
            title: '题型',
            dataIndex: 'type',
            width: 80,
            render: (type: QuestionType) => QUESTION_TYPE_LABELS[type],
          },
          {
            title: '知识域',
            dataIndex: 'domain',
            width: 100,
            render: (d: keyof typeof DOMAIN_LABELS) => DOMAIN_LABELS[d],
          },
          {
            title: '科目',
            dataIndex: 'stages',
            width: 180,
            render: (stages: (keyof typeof STAGE_META)[]) =>
              stages.length === 0 ? (
                <Tag>全月龄通用</Tag>
              ) : (
                stages.map((s) => <Tag key={s}>{STAGE_META[s].subject}</Tag>)
              ),
          },
          {
            title: '答错率',
            key: 'wrongRate',
            width: 100,
            sorter: true,
            sortDirections: ['descend', 'ascend'],
            sortOrder:
              query.sortBy === 'wrongRate'
                ? query.sortOrder === 'asc'
                  ? 'ascend'
                  : 'descend'
                : null,
            render: (_, row) =>
              // 样本不足时不显示百分比。编出来的数字一旦被戳穿，整个题库的可信度被连坐
              row.stat && row.stat.totalCount >= MIN_SAMPLE_FOR_STATS
                ? `${Math.round((row.stat.wrongCount / row.stat.totalCount) * 100)}%`
                : row.stat
                  ? `样本 ${row.stat.totalCount}`
                  : '—',
          },
          {
            title: '状态',
            dataIndex: 'status',
            width: 110,
            render: (s: QuestionStatus) => (
              <Badge color={STATUS_COLOR[s]} text={QUESTION_STATUS_LABELS[s]} />
            ),
          },
          {
            title: '',
            width: 60,
            render: (_, row) => (
              <Button type="link" size="small" onClick={() => navigate(`/questions/${row.id}`)}>
                详情
              </Button>
            ),
          },
        ]}
      />
    </Card>
  )
}
